/**
 * ESCHER FALLS — `PieceDef` から実体 `Piece`（占有セル・ポート・セグメント）への展開。
 *
 * MODULE-CONTRACTS.md §2 の展開規則をそのまま実装した純ロジック。
 * three.js にも DOM にも依存しない（CORE-SPEC §2 の依存規則）。
 */

import type {
  Axis6,
  Bounds,
  Piece,
  PieceDef,
  PieceId,
  Port,
  PortName,
  Segment,
  Turn,
  Vec3,
} from './types';

// =============================================================================
// 定数
// =============================================================================

/** 6方向の単位ベクトル。MODULE-CONTRACTS §2.1（固定値）。 */
export const AXIS_VECTORS: Record<Axis6, Vec3> = {
  '+x': [1, 0, 0],
  '-x': [-1, 0, 0],
  '+y': [0, 1, 0],
  '-y': [0, -1, 0],
  '+z': [0, 0, 1],
  '-z': [0, 0, -1],
};

const OPPOSITE_AXIS: Record<Axis6, Axis6> = {
  '+x': '-x',
  '-x': '+x',
  '+y': '-y',
  '-y': '+y',
  '+z': '-z',
  '-z': '+z',
};

/** `fall` が下へ、`pillar` が上へ伸びる基準ベクトル。 */
const UP: Vec3 = [0, 1, 0];

/** `waterwheel.dir` 省略時の既定（縦置き。MODULE-CONTRACTS §18-2）。 */
const DEFAULT_WHEEL_AXIS: Axis6 = '+y';

// =============================================================================
// 内部ユーティリティ
// =============================================================================

function isAxis6(value: unknown): value is Axis6 {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(AXIS_VECTORS, value);
}

/** `'+x'` → `'x'`。`corner` の「異なる軸」判定に使う。 */
function axisLetter(axis: Axis6): string {
  return axis.slice(1);
}

function fail(pieceId: PieceId, message: string): never {
  throw new Error(`pieces: ${pieceId}: ${message}`);
}

function axisVector(axis: Axis6, pieceId: PieceId, field: string): Vec3 {
  if (!isAxis6(axis)) fail(pieceId, `${field} が不正な軸である (${String(axis)})`);
  return AXIS_VECTORS[axis];
}

/** `p + s·v`。`p` が整数・`v` が単位軸ベクトル・`s` が `.5` 刻みなら結果は誤差なく厳密。 */
function addScaled(p: Vec3, v: Vec3, s: number): Vec3 {
  return [p[0] + v[0] * s, p[1] + v[1] * s, p[2] + v[2] * s];
}

function makePort(pieceId: PieceId, name: PortName, pos: Vec3, dir: Axis6): Port {
  return { id: `${pieceId}:${name}`, pieceId, name, pos, dir };
}

function makeSegment(pieceId: PieceId, from: Port, to: Port, cells: number): Segment {
  return { id: `${pieceId}:seg`, pieceId, fromPort: from.id, toPort: to.id, cells };
}

/** `p` を起点に `v` 方向へ `count` マス並べたセル列（`i` 昇順）。 */
function cellRun(p: Vec3, v: Vec3, count: number): Vec3[] {
  const cells: Vec3[] = [];
  for (let i = 0; i < count; i += 1) cells.push(addScaled(p, v, i));
  return cells;
}

// -- 事前条件の検査 -----------------------------------------------------------
// MODULE-CONTRACTS §0.5 では core は投げない建前だが、`expandPiece` は
// レベル JSON 由来の値をそのまま受ける唯一の入口である。壊れた定義を黙って
// 通すとポート座標が不変条件（`.5` 成分は高々1個）を破り、原因の分からない
// 誤接続になる。ここでは「起きてはならないこと」を早期に落とす防御に留める
// （正常な `Level` を流す限り決して投げない）。

function pieceIdOf(def: PieceDef, index: number): PieceId {
  if (def.id !== undefined) {
    if (typeof def.id !== 'string' || def.id.length === 0) {
      throw new Error(`pieces: pieces[${index}]: id は非空の文字列でなければならない`);
    }
    return def.id;
  }
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`pieces: index は 0 以上の整数でなければならない (${String(index)})`);
  }
  return `p${index}`;
}

function requireIntegerPos(pos: Vec3, pieceId: PieceId): void {
  const comps: readonly number[] = pos;
  if (comps.length !== 3 || !comps.every((c) => Number.isInteger(c))) {
    fail(pieceId, `pos は整数3成分でなければならない ([${comps.join(', ')}])`);
  }
}

function requireCellCount(value: number, pieceId: PieceId, field: string): number {
  if (!Number.isInteger(value) || value < 1) {
    fail(pieceId, `${field} は 1 以上の整数でなければならない (${String(value)})`);
  }
  return value;
}

function assertNever(def: never): never {
  throw new Error(`pieces: 未知のピース種別: ${JSON.stringify(def)}`);
}

// =============================================================================
// 公開 API
// =============================================================================

/** 軸の反転。`'+x'` ↔ `'-x'` など。 */
export function oppositeAxis(axis: Axis6): Axis6 {
  if (!isAxis6(axis)) throw new Error(`pieces: 不正な軸: ${String(axis)}`);
  return OPPOSITE_AXIS[axis];
}

/**
 * `PieceDef` を展開して `Piece` を作る。
 * `index` は `level.pieces` の配列添字（`def.id` 省略時の ID 採番に使う）。
 *
 * 同じ入力からは常に同じ ID・同じ順序の結果が出る（MODULE-CONTRACTS §0.4 / §0.6）。
 */
export function expandPiece(def: PieceDef, index: number): Piece {
  const id = pieceIdOf(def, index);
  requireIntegerPos(def.pos, id);
  const p = def.pos;

  switch (def.type) {
    case 'source': {
      const d = axisVector(def.dir, id, 'dir');
      const out = makePort(id, 'out', addScaled(p, d, 0.5), def.dir);
      return { id, def, ports: [out], segments: [], cells: [p] };
    }

    case 'goal':
    // 涸れ井戸はゴールと**同じ形**（ポート1つ）。違うのは意味だけで、
    // 展開の仕方を分ける理由が無い。
    case 'drain': {
      const d = axisVector(def.dir, id, 'dir');
      const into = makePort(id, 'in', addScaled(p, d, 0.5), def.dir);
      return { id, def, ports: [into], segments: [], cells: [p] };
    }

    case 'straight': {
      const d = axisVector(def.dir, id, 'dir');
      const length = requireCellCount(def.length, id, 'length');
      // a は基準セルの手前側の面、b は length マス先の面。両端の距離はちょうど length。
      const a = makePort(id, 'a', addScaled(p, d, -0.5), oppositeAxis(def.dir));
      const b = makePort(id, 'b', addScaled(p, d, length - 0.5), def.dir);
      return {
        id,
        def,
        ports: [a, b],
        segments: [makeSegment(id, a, b, length)],
        cells: cellRun(p, d, length),
      };
    }

    case 'corner': {
      const f = axisVector(def.from, id, 'from');
      const t = axisVector(def.to, id, 'to');
      if (axisLetter(def.from) === axisLetter(def.to)) {
        fail(id, `corner の from と to は異なる軸でなければならない (${def.from} / ${def.to})`);
      }
      const a = makePort(id, 'a', addScaled(p, f, 0.5), def.from);
      const b = makePort(id, 'b', addScaled(p, t, 0.5), def.to);
      return {
        id,
        def,
        ports: [a, b],
        segments: [makeSegment(id, a, b, 1)],
        cells: [p],
      };
    }

    case 'fall': {
      const height = requireCellCount(def.height, id, 'height');
      // pos は最上段のセル。水は下へ落ちる（MODULE-CONTRACTS §2.2 の警告）。
      const top = makePort(id, 'top', addScaled(p, UP, 0.5), '+y');
      const bottom = makePort(id, 'bottom', addScaled(p, UP, -(height - 0.5)), '-y');
      return {
        id,
        def,
        ports: [top, bottom],
        segments: [makeSegment(id, top, bottom, height)],
        cells: cellRun(p, AXIS_VECTORS['-y'], height),
      };
    }

    case 'waterwheel': {
      const axis = def.dir ?? DEFAULT_WHEEL_AXIS;
      const d = axisVector(axis, id, 'dir');
      const a = makePort(id, 'a', addScaled(p, d, -0.5), oppositeAxis(axis));
      const b = makePort(id, 'b', addScaled(p, d, 0.5), axis);
      return {
        id,
        def,
        ports: [a, b],
        segments: [makeSegment(id, a, b, 1)],
        cells: [p],
      };
    }

    case 'pillar': {
      const height = requireCellCount(def.height, id, 'height');
      // pos は最下段のセル。柱は地面から立ち上がる。ポートもセグメントも持たない。
      return { id, def, ports: [], segments: [], cells: cellRun(p, UP, height) };
    }

    case 'lever': {
      // レバー自体は水路ではない。1セルを占有するだけで、ポートもセグメントも持たない。
      return { id, def, ports: [], segments: [], cells: [p] };
    }

    case 'turntable': {
      // 回る足場の台。水路ではない。1セルを占めるだけ（円盤は見た目だけ広い）。
      requireCellCount(def.radius, id, 'radius');
      return { id, def, ports: [], segments: [], cells: [p] };
    }

    default:
      return assertNever(def);
  }
}

/** レベルの全 `PieceDef` を展開する。 */
/**
 * レバーの状態を定義に織り込む。`shift` を持つピースだけが動く。
 *
 * 展開の前に座標を確定させることで、`expandPiece` 以降は
 * 「レバーがある」ことを一切知らずに済む。
 */
export function applyLeverState(
  defs: readonly PieceDef[],
  leverOn: boolean,
): PieceDef[] {
  if (!leverOn) return [...defs];
  return defs.map((def) => {
    if (def.turn !== undefined) return turnDef(def, def.turn);
    if (def.shift === undefined) return def;
    return {
      ...def,
      pos: [
        def.pos[0] + def.shift[0],
        def.pos[1] + def.shift[1],
        def.pos[2] + def.shift[2],
      ] as Vec3,
    };
  });
}

/**
 * 水平の向きを上から見て反時計回りに 90° 回す。`(x, z) → (-z, x)`。
 * {@link turnPos} と同じ写像でなければならない（位置と向きがずれると口が外を向く）。
 */
const TURN_AXIS: Readonly<Record<Axis6, Axis6>> = {
  '+x': '+z',
  '+z': '-x',
  '-x': '-z',
  '-z': '+x',
  '+y': '+y',
  '-y': '-y',
};

export function turnAxis(axis: Axis6, quarters: number): Axis6 {
  let a = axis;
  for (let i = 0; i < quarters; i += 1) a = TURN_AXIS[a];
  return a;
}

/** 点を `about` を通る鉛直軸のまわりに回す。整数は整数のまま。 */
export function turnPos(pos: Vec3, turn: Turn): Vec3 {
  let dx = pos[0] - turn.about[0];
  let dz = pos[2] - turn.about[1];
  for (let i = 0; i < turn.quarters; i += 1) {
    const nx = -dz;
    dz = dx;
    dx = nx;
  }
  return [turn.about[0] + dx, pos[1], turn.about[1] + dz];
}

/** ピース定義を回す。向きを持つ欄（dir / from / to）も同じだけ回す。 */
function turnDef(def: PieceDef, turn: Turn): PieceDef {
  const pos = turnPos(def.pos, turn);
  const q = turn.quarters;
  switch (def.type) {
    case 'source':
    case 'goal':
    case 'drain':
    case 'straight':
      return { ...def, pos, dir: turnAxis(def.dir, q) };
    case 'waterwheel':
      return def.dir === undefined ? { ...def, pos } : { ...def, pos, dir: turnAxis(def.dir, q) };
    case 'corner':
      return { ...def, pos, from: turnAxis(def.from, q), to: turnAxis(def.to, q) };
    case 'fall':
    case 'pillar':
    case 'lever':
    case 'turntable':
      return { ...def, pos };
    default:
      return assertNever(def);
  }
}

export function expandPieces(defs: readonly PieceDef[], leverOn = false): Piece[] {
  return applyLeverState(defs, leverOn).map((def, index) => expandPiece(def, index));
}

/** この面にレバーがあるか。 */
export function hasLever(defs: readonly PieceDef[]): boolean {
  return defs.some((def) => def.type === 'lever');
}

/** 全ピースを平坦化した `Port` の配列。ピース順 → ピース内のポート順。 */
export function collectPorts(pieces: readonly Piece[]): Port[] {
  const ports: Port[] = [];
  for (const piece of pieces) ports.push(...piece.ports);
  return ports;
}

function findFirstPortOfType(pieces: readonly Piece[], type: PieceDef['type']): Port | null {
  for (const piece of pieces) {
    if (piece.def.type !== type) continue;
    const port: Port | undefined = piece.ports[0];
    if (port) return port;
  }
  return null;
}

/** `type === 'source'` のピースの唯一のポート。見つからなければ `null`。複数あれば最初のもの。 */
export function findSourcePort(pieces: readonly Piece[]): Port | null {
  return findFirstPortOfType(pieces, 'source');
}

/** `type === 'goal'` の最初のポート。見つからなければ `null`。 */
export function findGoalPort(pieces: readonly Piece[]): Port | null {
  return findFirstPortOfType(pieces, 'goal');
}

/**
 * すべてのゴールのポート。ピース順。
 *
 * ゴールは1つとは限らない。**全てに水が届いて初めてクリア**なので、
 * ある視点で片方を繋ぐともう片方が切れる、という葛藤が視点選びの中に生まれる。
 */
export function findGoalPorts(pieces: readonly Piece[]): Port[] {
  const out: Port[] = [];
  for (const piece of pieces) {
    if (piece.def.type !== 'goal') continue;
    out.push(...piece.ports);
  }
  return out;
}

/** 涸れ井戸のポート。**ここへ届く視点は解ではない。** */
export function findDrainPorts(pieces: readonly Piece[]): Port[] {
  const out: Port[] = [];
  for (const piece of pieces) {
    if (piece.def.type !== 'drain') continue;
    out.push(...piece.ports);
  }
  return out;
}

/** 全ピースの占有セルを含む境界ボックス。セル中心 ±0.5 まで広げた「見た目の箱」。 */
export function computeBounds(pieces: readonly Piece[]): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  let found = false;

  for (const piece of pieces) {
    for (const cell of piece.cells) {
      found = true;
      if (cell[0] < minX) minX = cell[0];
      if (cell[1] < minY) minY = cell[1];
      if (cell[2] < minZ) minZ = cell[2];
      if (cell[0] > maxX) maxX = cell[0];
      if (cell[1] > maxY) maxY = cell[1];
      if (cell[2] > maxZ) maxZ = cell[2];
    }
  }

  if (!found) {
    return { min: [0, 0, 0], max: [0, 0, 0], center: [0, 0, 0] };
  }

  const min: Vec3 = [minX - 0.5, minY - 0.5, minZ - 0.5];
  const max: Vec3 = [maxX + 0.5, maxY + 0.5, maxZ + 0.5];
  const center: Vec3 = [
    (min[0] + max[0]) / 2,
    (min[1] + max[1]) / 2,
    (min[2] + max[2]) / 2,
  ];
  return { min, max, center };
}
